import type { ReactNode } from "react";
import { ChevronRight, UserRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn, type DataTableServerPagination } from "@/components/ui/data-table";
import { UserAvatar } from "@/components/ui/user-avatar";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { useI18n } from "@/hooks/useI18n";
import { PaymentStatusCell } from "@/lib/dashboard/shared/PaymentStatusCell";
import { RequestsCountCell } from "@/lib/dashboard/shared/RequestsCountCell";
import { clientDisplayName } from "@/lib/dashboard/clients/client-display";
import { AgencyClientRow } from "@/integrations/supabase/rpc-types";

const dateFormatter = new Intl.DateTimeFormat("it-IT", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

interface ClientsTableProps {
  /** Solo le righe della pagina corrente. */
  rows: AgencyClientRow[];
  /** Riga aperta nel pannello: resta evidenziata sotto lo sheet. */
  selectedKey: string | null;
  onSelect: (row: AgencyClientRow) => void;
  showAdvisorColumn: boolean;
  pageSize: number;
  pagination: DataTableServerPagination;
  isLoading: boolean;
  /** `version` del caricamento (+ pagina): le righe rientrano animate a ogni cambio. */
  animationKey: string | number;
  emptyState: ReactNode;
}

export function ClientsTable({
  rows,
  selectedKey,
  onSelect,
  showAdvisorColumn,
  pageSize,
  pagination,
  isLoading,
  animationKey,
  emptyState,
}: ClientsTableProps) {
  const { t } = useI18n();

  const columns: DataTableColumn<AgencyClientRow>[] = [
    {
      id: "client",
      header: t("dashboard.overview.clients.columnClient"),
      cellClassName: "font-medium text-gray-900",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <UserAvatar
            expanded
            firstName={row.first_name}
            lastName={row.last_name}
            fullName={clientDisplayName(row)}
            email={row.email}
            imageUrl={row.avatar_url}
            size="sm"
          />
          {!row.has_account && (
            <InfoTooltip text={t("dashboard.overview.clients.noAccountInfo")} />
          )}
        </div>
      ),
    },
    ...(showAdvisorColumn
      ? [
          {
            id: "advisor",
            header: (
              <span className="inline-flex items-center gap-1.5">
                {t("dashboard.overview.clients.columnAdvisor")}
                <InfoTooltip text={t("dashboard.overview.clients.columnAdvisorInfo")} />
              </span>
            ),
            headClassName: "w-px whitespace-nowrap",
            cellClassName: "text-gray-600 whitespace-nowrap",
            cell: (row: AgencyClientRow) => {
              const advisorName =
                [row.advisor_first_name, row.advisor_last_name].filter(Boolean).join(" ") || null;
              if (!advisorName) return "—";
              return (
                <span className="inline-flex items-center gap-1.5">
                  {advisorName}
                  {/*
                    Un cliente puo' essere passato per piu' consulenti. I numeri
                    della riga li sommano tutti, quindi mostrare un nome solo e
                    tacere sugli altri farebbe attribuire a lui un valore che
                    non e' tutto suo.
                  */}
                  {row.advisor_count > 1 && (
                    <Badge
                      variant="outline"
                      className="text-xs font-normal cursor-default"
                      title={row.advisors
                        .map((a) =>
                          t("dashboard.overview.clients.advisorRequests", {
                            name: [a.first_name, a.last_name].filter(Boolean).join(" ") || a.advisor_id,
                            count: a.requests_count,
                          })
                        )
                        .join("\n")}
                    >
                      +{row.advisor_count - 1}
                    </Badge>
                  )}
                </span>
              );
            },
          },
        ]
      : []),
    {
      // Una cella sola: vedi RequestsCountCell.
      id: "requests",
      header: (
        <span className="inline-flex items-center gap-1.5">
          {t("dashboard.overview.requests.columnRequests")}
          <InfoTooltip text={t("dashboard.overview.clients.columnRequestsInfo")} />
        </span>
      ),
      headClassName: "w-px whitespace-nowrap text-right",
      cellClassName: "text-right",
      cell: (row) => (
        <RequestsCountCell activeCount={row.active_count} completedCount={row.completed_count} />
      ),
    },
    {
      // Valore chiuso e residuo erano due colonne di importi da sottrarre a mente: ora
      // sono una cella sola con totale, barra e i due addendi.
      id: "payment",
      header: (
        <span className="inline-flex items-center gap-1.5">
          {t("dashboard.overview.paymentStatus.column")}
          <InfoTooltip text={t("dashboard.overview.paymentStatus.infoClients")} />
        </span>
      ),
      headClassName: "w-px whitespace-nowrap text-right",
      cellClassName: "text-right",
      cell: (row) => (
        <PaymentStatusCell
          paidAmount={row.paid_amount}
          pendingAmount={row.remaining_amount}
          overdueAmount={row.overdue_amount}
          totalAmount={row.total_amount}
        />
      ),
    },
    {
      id: "lastActivity",
      header: t("dashboard.overview.clients.columnLastActivity"),
      headClassName: "w-px whitespace-nowrap text-right",
      cellClassName: "text-right text-gray-500 whitespace-nowrap",
      cell: (row) => dateFormatter.format(new Date(row.last_request_at)),
    },
    {
      id: "open",
      header: null,
      headClassName: "w-8",
      cellClassName: "text-right",
      cell: () => <ChevronRight className="h-4 w-4 text-gray-400" />,
    },
  ];

  return (
    <DataTable
      data={rows}
      columns={columns}
      getRowId={(row) => row.client_key}
      onRowClick={onSelect}
      selectedRowId={selectedKey}
      pageSize={pageSize}
      serverPagination={pagination}
      isLoading={isLoading}
      animationKey={animationKey}
      emptyState={emptyState}
      tableClassName="table-auto min-w-[900px]"
    />
  );
}

interface ClientsEmptyProps {
  filtered: boolean;
}

/** Vuoto per davvero e vuoto per colpa dei filtri sono due messaggi diversi. */
export function ClientsEmpty({ filtered }: ClientsEmptyProps) {
  const { t } = useI18n();

  return (
    <div className="flex flex-col items-center gap-2 text-center text-gray-500">
      <UserRound className="h-8 w-8 text-gray-300" />
      <p className="text-sm">
        {filtered
          ? t("dashboard.overview.clients.emptyFiltered")
          : t("dashboard.overview.clients.empty")}
      </p>
    </div>
  );
}

export default ClientsTable;
